// HomeownerWarrantyCard — drop-in replacement for the existing warranty info
// block on StatusPortal.
//
// Lists prior claims via the homeowner-warranty-claim edge function (RLS
// blocks anon read of warranty_claims, so we route through the same edge
// function that handles writes). "Report a warranty issue" CTA opens the
// HomeownerWarrantyForm modal. Each active claim also gets an "Add photos or
// files" control (Sep 15, 2026) so the homeowner can send more after filing —
// uploads land in homeowner-uploads/{deal}/warranty/{claim}/ and are registered
// through the edge function's add_photo kind.

import { useEffect, useState } from "react";
import { supabaseAnon } from "../../lib/supabase";
import { fmtSupabaseError } from "../../lib/fmtError";
import HomeownerWarrantyForm from "./HomeownerWarrantyForm";
import AttachmentPicker from "../shared/AttachmentPicker";
import {
  describeAttachmentCounts,
  describeFailures,
  uploadHomeownerWarrantyAttachments,
} from "../../lib/warrantyAttachments";

interface HomeownerWarrantyCardProps {
  deal: {
    id: string;
    homeowner_name: string | null;
    warranty_type: string | null;
    oc_warranty_number: string | null;
    warranty_registered_date: string | null;
    warranty_expiration_date?: string | null;
  };
  duckImg: string;
}

interface ClaimRow {
  id: string;
  title: string | null;
  category: string | null;
  status: string;
  claim_date: string | null;
  resolution_date: string | null;
  created_at: string;
  /** Returned by homeowner-warranty-claim v3+; absent on older deployments. */
  attachment_count?: number;
}

const STATUS_LABELS: Record<string, string> = {
  claim_filed: "Filed",
  in_progress: "In progress",
  claim_approved: "Approved",
  claim_denied: "Denied",
  voided: "Closed",
  expired: "Expired",
};
const STATUS_TONES: Record<string, string> = {
  claim_filed: "bg-amber-100 text-amber-700",
  in_progress: "bg-blue-100 text-blue-700",
  claim_approved: "bg-emerald-100 text-emerald-700",
  claim_denied: "bg-slate-200 text-slate-700",
  voided: "bg-slate-200 text-slate-700",
  expired: "bg-slate-200 text-slate-700",
};

function fmtDate(d: string | null | undefined): string {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return d;
  }
}

function getTokenFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const p = new URLSearchParams(window.location.search);
  return p.get("token");
}

/**
 * Call the homeowner-warranty-claim edge function via supabaseAnon.
 * Public endpoint (verify_jwt: false) — uses the anon key from supabaseAnon's
 * baked-in headers, no need to manage env vars in this component.
 */
export async function callWarrantyFn<T = unknown>(
  token: string,
  kind: string,
  payload: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabaseAnon.functions.invoke("homeowner-warranty-claim", {
    body: { token, kind, payload },
  });
  if (error) throw error;
  if (data && typeof data === "object" && "ok" in data && (data as { ok: boolean }).ok === false) {
    throw new Error(((data as { error?: string }).error) || "edge function returned ok:false");
  }
  return data as T;
}

export default function HomeownerWarrantyCard({ deal, duckImg }: HomeownerWarrantyCardProps) {
  const [claims, setClaims] = useState<ClaimRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const token = getTokenFromUrl();

  async function loadClaims() {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      const result = await callWarrantyFn<{ ok: true; claims: ClaimRow[] }>(token, "list_claims", {});
      setClaims(result.claims ?? []);
    } catch (e) {
      setErr(fmtSupabaseError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadClaims();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.id]);

  const activeClaims = claims.filter((c) => c.status === "claim_filed" || c.status === "in_progress");
  const pastClaims = claims.filter((c) => !(c.status === "claim_filed" || c.status === "in_progress"));

  return (
    <>
      <div className="bg-green-50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <img src={duckImg} alt="" className="w-6 h-6" />
          <span className="font-semibold text-green-800 text-sm">Warranty Active</span>
        </div>
        <div className="space-y-1 text-sm">
          <InfoRow label="Type" value={deal.warranty_type || "Owens Corning Preferred Protection"} />
          {deal.oc_warranty_number && <InfoRow label="Warranty #" value={deal.oc_warranty_number} />}
          <InfoRow label="Registered" value={fmtDate(deal.warranty_registered_date)} />
          {deal.warranty_expiration_date && <InfoRow label="Expires" value={fmtDate(deal.warranty_expiration_date)} />}
        </div>

        {/* Active claims */}
        {!loading && activeClaims.length > 0 && (
          <div className="mt-3 bg-white border border-amber-200 rounded p-3">
            <div className="text-xs font-semibold text-amber-700 mb-1.5">
              {activeClaims.length} active claim{activeClaims.length === 1 ? "" : "s"}
            </div>
            <ul className="space-y-2">
              {activeClaims.map((c) => (
                <li key={c.id} className="text-xs text-slate-700">
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex-1 min-w-0 truncate">{c.title || "(no title)"}</span>
                    <AttachmentCountChip count={c.attachment_count} />
                    <span className={"px-2 py-0.5 rounded flex-shrink-0 " + (STATUS_TONES[c.status] ?? "bg-slate-100 text-slate-600")}>
                      {STATUS_LABELS[c.status] ?? c.status}
                    </span>
                  </div>
                  {token && (
                    <ClaimAttachmentAdder token={token} dealId={deal.id} claimId={c.id} onAdded={loadClaims} />
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Past claims (collapsed) */}
        {!loading && pastClaims.length > 0 && (
          <details className="mt-3 bg-white border border-slate-200 rounded p-3">
            <summary className="text-xs font-semibold text-slate-700 cursor-pointer">
              {pastClaims.length} past claim{pastClaims.length === 1 ? "" : "s"}
            </summary>
            <ul className="mt-2 space-y-1.5">
              {pastClaims.map((c) => (
                <li key={c.id} className="text-xs text-slate-600 flex items-center justify-between gap-2">
                  <span className="flex-1 min-w-0 truncate">
                    {c.title || "(no title)"}
                    {c.resolution_date ? ` · ${fmtDate(c.resolution_date)}` : ""}
                  </span>
                  <AttachmentCountChip count={c.attachment_count} />
                  <span className={"px-2 py-0.5 rounded flex-shrink-0 " + (STATUS_TONES[c.status] ?? "bg-slate-100 text-slate-600")}>
                    {STATUS_LABELS[c.status] ?? c.status}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}

        {err && (
          <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            Couldn't load claim history. {err}
          </div>
        )}

        <button
          onClick={() => setOpenForm(true)}
          disabled={!token}
          className="mt-3 w-full px-3 py-2 text-sm font-medium rounded bg-bardin-orange text-white hover:bg-bardin-orange/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          🛠️ Report a warranty issue
        </button>

        <p className="mt-2 text-xs text-slate-600 leading-relaxed">
          Spot a leak, missing shingle, or other issue? Report it here. Jennifer Ayers, our
          Director of Operations, gets notified right away and will reach out to schedule an
          inspection.
        </p>

        {!token && (
          <p className="mt-2 text-xs text-amber-700">
            We couldn't read the access link. Please use the link your project manager texted you,
            or call (470) 823-8816.
          </p>
        )}
      </div>

      {openForm && token && (
        <HomeownerWarrantyForm
          token={token}
          dealId={deal.id}
          dealName={deal.homeowner_name}
          onClose={() => setOpenForm(false)}
          onCreated={async () => {
            setOpenForm(false);
            await loadClaims();
          }}
        />
      )}
    </>
  );
}

function AttachmentCountChip({ count }: { count: number | undefined }) {
  if (!count || count <= 0) return null;
  return (
    <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 flex-shrink-0" title={`${count} photo${count === 1 ? "" : "s"}/file${count === 1 ? "" : "s"} on this claim`}>
      📎 {count}
    </span>
  );
}

// Per-claim "Add photos or files" — collapsed to a text link so the claim
// list stays compact; expands to the three-way picker.
function ClaimAttachmentAdder({ token, dealId, claimId, onAdded }: { token: string; dealId: string; claimId: string; onAdded: () => void | Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [msg, setMsg] = useState<{ tone: "ok" | "err"; text: string } | null>(null);

  async function handleFiles(files: File[]) {
    if (files.length === 0) return;
    setBusy(true);
    setMsg(null);
    setProgress({ done: 0, total: files.length });
    try {
      const result = await uploadHomeownerWarrantyAttachments({
        dealId,
        claimId,
        files,
        onProgress: (done, total) => setProgress({ done, total }),
      });
      if (result.uploaded.length > 0) {
        await callWarrantyFn(token, "add_photo", { claim_id: claimId, photos: result.uploaded });
      }
      const added = describeAttachmentCounts(result.photoCount, result.documentCount);
      if (result.failures.length > 0) {
        const n = result.failures.length;
        setMsg({
          tone: "err",
          text:
            (result.uploaded.length > 0 ? `Added ${added}. ` : "") +
            `${n === 1 ? "1 file" : `${n} files`} couldn't be added: ${describeFailures(result.failures)}`,
        });
      } else {
        setMsg({ tone: "ok", text: `Added ${added} — thank you! Jennifer has been notified.` });
      }
      await onAdded();
    } catch (e) {
      setMsg({ tone: "err", text: "Couldn't add files: " + fmtSupabaseError(e) });
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-1 text-xs font-medium text-bardin-orange hover:underline"
      >
        + Add photos or files
      </button>
    );
  }

  return (
    <div className="mt-1.5 bg-orange-50/60 border border-orange-200 rounded p-2">
      <AttachmentPicker onFiles={handleFiles} disabled={busy} tone="homeowner" />
      {busy && progress && (
        <div className="mt-1.5 text-xs text-slate-600">
          Uploading {Math.min(progress.done + 1, progress.total)} of {progress.total}…
        </div>
      )}
      {msg && (
        <div
          className={
            "mt-1.5 text-xs rounded px-2 py-1 border " +
            (msg.tone === "ok"
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-red-50 text-red-700 border-red-200")
          }
        >
          {msg.text}
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-gray-500">{label}:</span>
      <span className="font-medium text-bardin-ink">{value}</span>
    </div>
  );
}
